import { IS_PAID_USER, platform } from 'reactApp/appHelpers/configHelpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendDwh, sendXray } from 'reactApp/utils/ga';
import { getUsedTens } from 'reactApp/utils/getUsedTens';

import type { Actions } from './dwh.types';

type Ttypes =
    | 'modal'
    | 'dialog'
    | 'button'
    | 'balloon'
    | 'tooltip'
    | 'tariffs-quota'
    | 'banner'
    | 'button-info'
    | 'icon-info'
    | 'union-quota'
    | 'object'
    | 'viewtlbar'
    | 'compare'
    | 'err';

interface RadarArgs {
    action?: Actions;
    name?: string | null;
    button_name?: string;
    source?: string;
    place?: string;
    data?: Record<string, string | number | boolean>;
    dwh_enabled?: boolean;
    dwh_enabled_fields?: string[][];
}

export const sendDwhRadar = ({
    action = 'show',
    name = '',
    data,
    button_name,
    t = 'modal',
    place,
    source,
    dwh_enabled,
    dwh_enabled_fields,
}: RadarArgs & { t?: Ttypes }) => {
    const bttnName = button_name ? `${button_name}` : '';
    sendXray([`dwh_${t}`, name ?? 'nn', action, data?.byclick ? 'byclick' : '', bttnName, platform]);

    sendDwh({
        eventCategory: t,
        action,
        dwh_enabled,
        dwh_enabled_fields,
        dwhData: {
            name,
            paid_user: IS_PAID_USER,
            platform,
            button_name,
            place,
            source,
            ...data,
        },
    });
};

export const sendDwhModal = ({ action = 'show', name, data, button_name, place, dwh_enabled_fields }: RadarArgs) =>
    sendDwhRadar({
        action,
        name,
        data,
        button_name,
        place,
        t: 'modal',

        dwh_enabled: !!dwh_enabled_fields,
        dwh_enabled_fields,
    });

export const sendDwhDialog = ({ action = 'show', name, data, button_name, place, dwh_enabled_fields }: RadarArgs) =>
    sendDwhRadar({
        action,
        name,
        data,
        button_name,
        place,
        t: 'dialog',

        dwh_enabled: !!dwh_enabled_fields,
        dwh_enabled_fields,
    });

export const sendDwhButton = ({ action = 'show', name, data, source, button_name, dwh_enabled_fields }: RadarArgs) =>
    sendDwhRadar({
        action,
        name,
        data,
        button_name,
        source,
        t: 'button',

        dwh_enabled: !!dwh_enabled_fields,
        dwh_enabled_fields,
    });

export const sendDwhTooltip = ({ action = 'show', name, data, place, button_name, dwh_enabled_fields }: RadarArgs) =>
    sendDwhRadar({
        action,
        name,
        data,
        button_name,
        place,
        t: 'tooltip',

        dwh_enabled: !!dwh_enabled_fields,
        dwh_enabled_fields,
    });

export const sendDwhBanner = ({ action = 'show', name, data, place, button_name, dwh_enabled_fields }: RadarArgs) =>
    sendDwhRadar({
        action,
        name,
        data,
        button_name,
        place,
        t: 'banner',

        dwh_enabled: !!dwh_enabled_fields,
        dwh_enabled_fields,
    });

export const getAccountDwhChains = (idChain: string[] = []) => {
    const state = reduxStore.getState();
    const accountType = UserSelectors.getAccountType(state);
    const isCorp = UserSelectors.isCorpUser(state);
    const isPaid = UserSelectors.isPaidUser(state);
    const overquotaType = UserQuotaSelectors.getOverquotaType(state) || 'null';
    const { quota, used } = UserQuotaSelectors.getQuotaState(state);

    const usedBytes = used?.total?.original;
    const totalBytes = quota?.original;

    let usedTens: 'over' | 'none' | number = 'none';

    if (typeof totalBytes === 'number' && typeof usedBytes === 'number') {
        usedTens = getUsedTens(usedBytes, totalBytes);
    }

    const dwhData = {
        account_type: accountType,
        is_corp: isCorp,
        is_paid: isPaid,
        used: usedTens === 'over' ? overquotaType : usedTens,
    };

    const dwhChains: (keyof typeof dwhData)[][] = [['account_type', 'is_corp', 'is_paid', 'used']];

    return { dwhData, dwhChains: dwhChains.map((chain) => [...idChain, ...chain]) };
};
